import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { usePrefersReducedMotion } from '@/components/animations';
import { useCartUi } from './cart-ui-store';
// ---------------------------------------------------------------------------
// Single animated fly-to element
// Note: framer-motion x/y shorthands run on rAF (main thread). Acceptable for
// this 0.6s one-shot effect; the trade-off is documented here intentionally.
// ---------------------------------------------------------------------------
const EASE_ARTISAN = [0.22, 1, 0.36, 1];
function FlyItem({ entry }) {
    const removeFlyTo = useCartUi((s) => s.removeFlyTo);
    const notifyLanded = useCartUi((s) => s.notifyLanded);
    const targetRect = useCartUi.getState().getAnchorRect();
    // No anchor mounted - skip render, clean up immediately
    useEffect(() => {
        if (!targetRect) {
            removeFlyTo(entry.key);
            notifyLanded();
        }
    }, [entry.key, notifyLanded, removeFlyTo, targetRect]);
    if (!targetRect)
        return null;
    const fromCx = entry.fromRect.left + entry.fromRect.width / 2;
    const fromCy = entry.fromRect.top + entry.fromRect.height / 2;
    const toCx = targetRect.left + targetRect.width / 2;
    const toCy = targetRect.top + targetRect.height / 2;
    const dx = toCx - fromCx;
    const dy = toCy - fromCy;
    return (_jsx(motion.img, { src: entry.imageUrl, alt: "", "aria-hidden": "true", draggable: false, className: "pointer-events-none rounded-md object-cover", style: {
            position: 'fixed',
            top: entry.fromRect.top,
            left: entry.fromRect.left,
            width: entry.fromRect.width,
            height: entry.fromRect.height,
        }, initial: { x: 0, y: 0, scale: 1, opacity: 1 }, animate: {
            x: [0, dx * 0.5, dx],
            y: [0, dy * 0.3, dy],
            scale: [1, 0.6, 0.2],
            opacity: [1, 1, 0],
        }, transition: { duration: 0.6, ease: EASE_ARTISAN }, onAnimationComplete: () => {
            removeFlyTo(entry.key);
            notifyLanded();
        } }));
}
// ---------------------------------------------------------------------------
// Root overlay - single portal, renders all pending fly-to entries
// ---------------------------------------------------------------------------
export function FlyToCartOverlay() {
    const pendingFlyTo = useCartUi((s) => s.pendingFlyTo);
    const removeFlyTo = useCartUi((s) => s.removeFlyTo);
    const notifyLanded = useCartUi((s) => s.notifyLanded);
    const prefersReduced = usePrefersReducedMotion();
    // Reduced motion: skip animation entirely, still fire notifyLanded for badge
    useEffect(() => {
        if (prefersReduced && pendingFlyTo.length > 0) {
            pendingFlyTo.forEach((e) => removeFlyTo(e.key));
            notifyLanded();
        }
    }, [prefersReduced, pendingFlyTo, removeFlyTo, notifyLanded]);
    if (pendingFlyTo.length === 0 || prefersReduced)
        return null;
    return createPortal(_jsx("div", { "aria-hidden": "true", className: "pointer-events-none fixed inset-0", style: { zIndex: 180 }, children: pendingFlyTo.map((entry) => (_jsx(FlyItem, { entry: entry }, entry.key))) }), document.body);
}
